import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Masonry, MasonryCard } from "@/components/Masonry";

const MIN = 5, MAX = 8;

export default function StyleFeed() {
  const navigate = useNavigate();
  const { session, update } = useSession();
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState("All");
  const [picked, setPicked] = useState(session.selected_styles || []);

  useEffect(() => {
    api.get("/images", { params: { category: "style" } }).then(({ data }) => setImages(shuffle(data)));
  }, []);

  const styles = useMemo(() => ["All", ...Array.from(new Set(images.map((i) => i.style)))], [images]);
  const visible = filter === "All" ? images : images.filter((i) => i.style === filter);
  const ids = new Set(picked.map((p) => p.id));

  const toggle = (item) => {
    if (ids.has(item.id)) return setPicked(picked.filter((p) => p.id !== item.id));
    if (picked.length >= MAX) return toast(`${MAX} is plenty — swap one out if you must.`);
    setPicked([...picked, { id: item.id, url: item.url, title: item.title, style: item.style }]);
  };

  const next = async () => {
    const changed = JSON.stringify(picked.map((p) => p.id)) !== JSON.stringify((session.selected_styles || []).map((p) => p.id));
    await update({ selected_styles: picked, step: Math.max(session.step, 2), ...(changed ? { palettes: [], chosen_palette: null } : {}) });
    navigate("/palette");
  };

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-8 pt-12 pb-40">
      <div className="max-w-3xl">
        <p className="eyebrow rise">Step two · Taste</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 rise d1">Scroll. Tap what feels like <span className="italic">home</span>.</h1>
        <p className="text-neutral-500 mt-4 rise d2">Pick {MIN} to {MAX} spaces you'd happily walk into. Don't overthink it — your eye already knows.</p>
      </div>

      <div data-testid="style-filter-bar" className="flex gap-2 overflow-x-auto mt-10 pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none]">
        {styles.map((s) => (
          <button key={s} data-testid={`style-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setFilter(s)} className={`chip whitespace-nowrap ${filter === s ? "chip-on" : ""}`}>{s}</button>
        ))}
      </div>

      <div className="mt-8">
        <Masonry>
          {visible.map((item) => (
            <MasonryCard key={item.id} item={item} selected={ids.has(item.id)} onToggle={toggle} />
          ))}
        </Masonry>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-30 pointer-events-none">
        <div className="max-w-3xl mx-auto px-4 pb-6">
          <div className="pointer-events-auto bg-neutral-900 text-white rounded-full h-16 pl-6 pr-2 flex items-center justify-between shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {picked.slice(0, 5).map((p) => <img key={p.id} src={p.url} alt="" className="h-9 w-9 rounded-full object-cover border-2 border-neutral-900" />)}
              </div>
              <span data-testid="style-pick-count" className="text-sm">
                {picked.length < MIN ? `${picked.length} of ${MIN} chosen` : `${picked.length} chosen · lovely`}
              </span>
            </div>
            <button data-testid="styles-continue-button" onClick={next} disabled={picked.length < MIN} className="h-12 rounded-full bg-white text-neutral-900 px-6 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-30 transition-opacity">
              Colours next <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
